import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, InputBase } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type ExpandingSearchBoxProps = {
  value: string;
  onSearch: (term: string) => void;
  placeholderLabel?: string;
  debounceMs?: number;
};

const ExpandingSearchBox = ({
  value,
  onSearch,
  placeholderLabel = "",
  debounceMs = 500,
}: ExpandingSearchBoxProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(Boolean(value));
  const [term, setTerm] = useState<string>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const isFirstRun = useRef(true);

  useEffect(() => {
    setTerm(value);
    setExpanded(Boolean(value));
  }, [value]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const handle = setTimeout(() => {
      onSearch(term.trimStart());
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [term]);

  const handleContainerClick = () => {
    setExpanded(true);
    inputRef.current?.focus();
  };

  const handleClear = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setTerm("");
    setExpanded(false);
    inputRef.current?.blur();
    onSearch("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      handleClear();
    }
  };

  const handleBlur = () => {
    if (!term) {
      setExpanded(false);
    }
  };

  return (
    <Box
      onClick={handleContainerClick}
      data-testid={expanded ? "content-listing-search-input" : "content-listing-search-toggle"}
      sx={{
        backgroundColor: "white",
        padding: { xs: "0 8px", md: "0 10px" },
        borderRadius: "4px",
        border: "1px solid #14142B",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        height: { xs: "42px", md: "46px" },
        width: { xs: "100%", md: "300px" },
        maxWidth: expanded ? { xs: "480px", md: "300px" } : "42px",
        overflow: "hidden",
        cursor: expanded ? "default" : "pointer",
        transition: "max-width 0.3s ease",
      }}>
      <SearchIcon sx={{ color: "#2d2d39", fontSize: "21px", flexShrink: 0 }} />
      <InputBase
        inputRef={inputRef}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={`${t("search")} ${placeholderLabel}`.trim()}
        inputProps={{
          "aria-label": t("search"),
          "data-testid": "content-listing-search-field",
          tabIndex: expanded ? 0 : -1,
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "14px",
          color: "#2d2d39",
          opacity: expanded ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      />
      {expanded && term && (
        <IconButton
          size='small'
          aria-label='clear-search'
          data-testid='content-listing-search-clear'
          onClick={handleClear}
          sx={{ color: "#8991A5", padding: "2px", flexShrink: 0 }}>
          <CloseIcon sx={{ fontSize: "18px" }} />
        </IconButton>
      )}
    </Box>
  );
};

export default ExpandingSearchBox;
